/**
 * Endpoint de recepción — backend REAL.
 * GET /api/recepcion/pendientes?glnId=...&maxRemitos=...&remito=...&factura=...
 *
 * Devuelve remitos agrupados (1 card == 1 remito en el front).
 * `remito`/`factura` filtran server-side (excluyentes entre sí).
 *
 * NOTA: el módulo viejo `recepcion.ts` es del mock — no migrarlo todavía,
 * todavía lo usan los componentes de operaciones del flujo viejo.
 */

import {
  GetRecepcionPendientesResponse,
  type GetRecepcionPendientes,
} from '@/api/contracts/recepcion-pendientes';
import {
  RecepcionDetalleResponse,
  type RecepcionDetalle,
  ConfirmarIngresoResponseSchema,
  type ConfirmarIngresoResponse,
} from '@/api/contracts/recepcion-detalle';
import {
  RecepcionCompletadasResponse,
  type RecepcionCompletadas,
  type RecepcionCompletadasFilters,
} from '@/api/contracts/recepcion-completadas';
import { apiFetch } from '@/api/http';

export interface RecepcionPendientesFilters {
  /** Tope de remitos a traer. Default lo decide el backend (3). */
  maxRemitos?: number;
  /** Filtro por n° de remito (excluyente con factura). */
  remito?: string;
  /** Filtro por n° de factura (excluyente con remito). */
  factura?: string;
}

export async function getRecepcionPendientes(
  glnId: string | number,
  filters: RecepcionPendientesFilters = {},
): Promise<GetRecepcionPendientes> {
  const params = new URLSearchParams({ glnId: String(Number(glnId)) });
  if (filters.maxRemitos) params.set('maxRemitos', String(filters.maxRemitos));
  if (filters.remito) params.set('remito', filters.remito);
  else if (filters.factura) params.set('factura', filters.factura);

  const data = await apiFetch<unknown>(`/recepcion/pendientes?${params.toString()}`, {
    auth: true,
  });
  return GetRecepcionPendientesResponse.parse(data);
}

export interface RecepcionDetalleFilters {
  numeroRemito?: string | null;
  numeroFactura?: string | null;
}

/**
 * Detalle de una operación: el backend intenta primero por remito (si hay)
 * y hace fallback a factura. Devuelve items + cómo se encontró + log de
 * intentos.
 */
export async function getRecepcionDetalle(
  glnId: string | number,
  filters: RecepcionDetalleFilters,
): Promise<RecepcionDetalle> {
  const params = new URLSearchParams({ glnId: String(Number(glnId)) });
  if (filters.numeroRemito) params.set('numeroRemito', filters.numeroRemito);
  if (filters.numeroFactura) params.set('numeroFactura', filters.numeroFactura);
  const data = await apiFetch<unknown>(`/recepcion/detalle?${params.toString()}`, {
    auth: true,
  });
  return RecepcionDetalleResponse.parse(data);
}

export interface ConfirmarIngresoRequest {
  glnId: string | number;
  numeroRemito?: string | null;
  numeroFactura?: string | null;
}

/**
 * Confirma todas las transacciones pendientes de un remito o factura.
 * Backend → riga `/api/in/trazar-pendientes` (síncrono, no async).
 */
export async function confirmarIngreso(
  req: ConfirmarIngresoRequest,
): Promise<ConfirmarIngresoResponse> {
  const data = await apiFetch<unknown>('/recepcion/confirmar', {
    method: 'POST',
    body: {
      glnId: Number(req.glnId),
      numeroRemito: req.numeroRemito ?? null,
      numeroFactura: req.numeroFactura ?? null,
    },
    auth: true,
  });
  return ConfirmarIngresoResponseSchema.parse(data);
}

/**
 * Recepciones completadas (anmat_task estado='trazado') del GLN, con filtros
 * básicos (search + rango de fechas sobre traced_at).
 */
export async function getRecepcionCompletadas(
  glnId: string | number,
  filters: RecepcionCompletadasFilters = {},
): Promise<RecepcionCompletadas> {
  const params = new URLSearchParams({ glnId: String(Number(glnId)) });
  if (filters.search) params.set('search', filters.search);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  const data = await apiFetch<unknown>(
    `/recepcion/completadas?${params.toString()}`,
    { auth: true },
  );
  return RecepcionCompletadasResponse.parse(data);
}
